import React, { useState, useEffect, useMemo } from 'react';
import { Table, Button, Form, Modal, Row, Col, Badge, InputGroup, Spinner, Alert, Card } from 'react-bootstrap';
import { FaPlus, FaSearch, FaList, FaChartBar, FaEdit, FaPoll, FaTrash } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import api from '../../../api/apiClient';
import { toast } from 'react-toastify';

const OrgSurveyList = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const organizationId = user?.organization_id;

  const [surveys, setSurveys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [editingSurvey, setEditingSurvey] = useState(null);
  const [modalLoading, setModalLoading] = useState(false);
  const [courses, setCourses] = useState([]);
  const [imagePreview, setImagePreview] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    course_id: '',
    image: null,
    status: 1
  });
  const [formErrors, setFormErrors] = useState({});
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [surveyToDelete, setSurveyToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  useEffect(() => {
    if (!organizationId) {
      return;
    }
    fetchSurveys();
    fetchCourses();
  }, [organizationId]);

  const fetchSurveys = async () => {
    if (!organizationId) {
      return;
    }

    try {
      setLoading(true);
      const response = await api.surveys.getAll({ page: 1, limit: 100 });

      let surveyData = [];
      if (response?.data?.data && Array.isArray(response.data.data)) {
        surveyData = response.data.data;
      } else if (Array.isArray(response?.data)) {
        surveyData = response.data;
      } else {
        console.warn('Unexpected survey API response format:', response);
      }

      // Keep org surveys + global surveys (do not strip organization_id = null)
      const visibleSurveys = surveyData.filter((survey) => {
        if (survey?.survey_type === 'global') return true;
        return Number(survey?.organization_id) === Number(organizationId);
      });
      setSurveys(visibleSurveys);
    } catch (error) {
      console.error('Error fetching surveys:', error);
      toast.error('Failed to load surveys: ' + (error.response?.data?.message || error.message));
      setSurveys([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchCourses = async () => {
    if (!organizationId) {
      return;
    }

    try {
      const response = await api.organizations.getCourses(organizationId);
      let courseData = [];

      if (response?.data?.data && Array.isArray(response.data.data)) {
        courseData = response.data.data;
      } else if (Array.isArray(response?.data)) {
        courseData = response.data;
      }

      const normalized = courseData
        .map((item) => item.course || item)
        .filter(Boolean)
        .map((course) => ({
          id: course.id,
          name: course.name || course.title,
        }));

      setCourses(normalized);
    } catch (error) {
      console.error('Error fetching courses for surveys:', error);
      setCourses([]);
    }
  };

  const handleSearch = (event) => {
    setSearchTerm(event.target.value);
  };

  const handleFilterChange = (event) => {
    setFilterStatus(event.target.value);
  };

  const filteredSurveys = useMemo(() => {
    if (!Array.isArray(surveys)) {
      return [];
    }

    const search = searchTerm.trim().toLowerCase();

    return surveys.filter((survey) => {
      const title = survey.title || '';
      const description = survey.description || '';

      const matchesSearch =
        title.toLowerCase().includes(search) || description.toLowerCase().includes(search);

      let matchesStatus = true;
      if (filterStatus === 'active') {
        matchesStatus = survey.status === 1 && !survey.deleted;
      } else if (filterStatus === 'inactive') {
        matchesStatus = survey.status === 0 && !survey.deleted;
      }

      return matchesSearch && matchesStatus;
    });
  }, [surveys, searchTerm, filterStatus]);

  const getStatusBadge = (survey) => {
    if (survey.deleted) {
      return <Badge bg="danger">Deleted</Badge>;
    }

    return survey.status === 1 ? <Badge bg="success">Active</Badge> : <Badge bg="secondary">Inactive</Badge>;
  };

  const handleShowModal = (survey = null) => {
    if (!organizationId) {
      return;
    }

    if (survey) {
      setEditingSurvey(survey);
      setFormData({
        title: survey.title || '',
        description: survey.description || '',
        course_id: survey.course_id || '',
        image: null,
        status: survey.status ?? 1,
      });

      if (survey.image_path) {
        const baseUrl = process.env.REACT_APP_API_BASE_URL?.replace('/api', '') || '';
        setImagePreview(`${baseUrl}${survey.image_path}`);
      } else {
        setImagePreview(null);
      }
    } else {
      setEditingSurvey(null);
      setFormData({
        title: '',
        description: '',
        course_id: '',
        image: null,
        status: 1,
      });
      setImagePreview(null);
    }

    setFormErrors({});
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingSurvey(null);
    setFormData({
      title: '',
      description: '',
      course_id: '',
      image: null,
      status: 1,
    });
    setImagePreview(null);
    setFormErrors({});
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (formErrors[name]) {
      setFormErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const handleImageChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      toast.error('Please select a valid image file (JPEG, PNG, GIF, WEBP)');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image size must be less than 5MB');
      return;
    }

    setFormData((prev) => ({
      ...prev,
      image: file,
    }));

    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.title.trim()) {
      errors.title = 'Survey title is required';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!organizationId) {
      toast.error('Organization information is missing');
      return;
    }

    if (!validateForm()) {
      return;
    }

    try {
      setModalLoading(true);

      const submitData = new FormData();
      submitData.append('title', formData.title.trim());
      submitData.append('description', formData.description || '');
      submitData.append('survey_type', 'organization');
      submitData.append('organization_id', organizationId);

      if (formData.course_id) {
        submitData.append('course_id', formData.course_id);
      }

      if (formData.image) {
        submitData.append('image', formData.image);
      }

      submitData.append('status', formData.status);

      if (editingSurvey) {
        await api.surveys.update(editingSurvey.id, submitData);
        toast.success('Survey updated successfully');
      } else {
        await api.surveys.create(submitData);
        toast.success('Survey created successfully');
      }

      handleCloseModal();
      fetchSurveys();
    } catch (error) {
      console.error('Error saving survey:', error);
      toast.error(error.response?.data?.message || 'Failed to save survey');
    } finally {
      setModalLoading(false);
    }
  };

  const handleDeleteSurvey = (survey) => {
    setSurveyToDelete(survey);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!surveyToDelete) return;

    try {
      setDeleteLoading(true);
      await api.surveys.delete(surveyToDelete.id);
      toast.success('Survey deleted successfully');
      setShowDeleteModal(false);
      setSurveyToDelete(null);
      fetchSurveys();
    } catch (error) {
      console.error('Error deleting survey:', error);
      toast.error(error.response?.data?.message || 'Failed to delete survey');
    } finally {
      setDeleteLoading(false);
    }
  };

  if (!organizationId) {
    return (
      <div className="p-3 sm:p-4 md:p-6 bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50 min-h-screen">
        <div className="flex justify-center items-center" style={{ height: '400px' }}>
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-purple-600 border-t-transparent"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-3 sm:p-4 md:p-6 bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50 min-h-screen">
      <div className="mb-4">
        <h5 className="fw-bold mb-0 text-xl sm:text-2xl md:text-3xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-pink-600">
          Organization Surveys
        </h5>
      </div>

      <Row className="mb-4 g-2 sm:g-3">
        <Col xs={12} md={6} className="mb-2 mb-md-0">
          <div className="flex shadow-sm rounded-lg sm:rounded-xl overflow-hidden bg-white">
            <div className="flex items-center px-3 bg-white border-r border-gray-200">
              <FaSearch className="w-4 h-4 text-purple-600" />
            </div>
            <input
              type="text"
              placeholder="Search by title or description..."
              value={searchTerm}
              onChange={handleSearch}
              className="w-full px-3 py-2 border-0 focus:ring-2 focus:ring-purple-500 text-sm sm:text-base outline-none"
            />
          </div>
        </Col>
        <Col xs={12} sm={6} md={3} className="mb-2 mb-md-0">
          <Form.Select 
            value={filterStatus} 
            onChange={handleFilterChange}
            className="rounded-lg sm:rounded-xl shadow-sm border-purple-200 focus:ring-2 focus:ring-purple-500 text-xs sm:text-sm"
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </Form.Select>
        </Col>
        <Col xs={12} sm={6} md={3}>
          <Button
            bsPrefix="custom-hamburger"
            onClick={() => handleShowModal()}
            className="w-full border-1 border-purple-600 bg-white text-purple-600 hover:from-purple-700 hover:to-pink-700 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm h-full flex items-center justify-center gap-2 p-2"
          >
            <FaPlus className="w-4 h-4" />
            <span className="whitespace-nowrap">Add Survey</span>
          </Button>
        </Col>
      </Row>

      <Card className="border-0 shadow-lg rounded-xl overflow-hidden">
        <Card.Body className="p-0">
          {loading ? (
            <div className="text-center py-10 bg-white">
              <div className="animate-spin rounded-full h-12 w-12 border-4 border-purple-600 border-t-transparent mx-auto"></div>
              <p className="mt-3 text-gray-600">Loading surveys...</p>
            </div>
          ) : filteredSurveys.length === 0 ? (
            <div className="py-10 bg-white flex flex-col items-center justify-center text-center">
              <FaPoll className="text-gray-300 mb-3" style={{ fontSize: '3rem' }} />
              <h5 className="text-gray-600 mb-2">No surveys found</h5>
              <p className="text-muted text-sm">Create your first survey to start collecting insights.</p>
            </div>
       
          ) : (
            <div className="table-responsive">
              <Table hover className="mb-0">
                <thead className="bg-gradient-to-r from-purple-600 to-pink-600 text-white">
                  <tr>
                    <th className="py-2 px-3">Title</th>
                    <th className="py-2 px-3">Type</th>
                    <th className="py-2 px-3">Status</th>
                    <th className="py-2 px-3">Linked Course</th>
                    <th className="py-2 px-3">Questions</th>
                    <th className="py-2 px-3">Created</th>
                    <th className="py-2 px-3 text-end">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredSurveys.map((survey) => {
                    const isGlobalSurvey = survey.survey_type === 'global' || survey.organization_id == null;
                    return (
                    <tr key={survey.id} className="border-b border-purple-50 hover:bg-purple-50/60 transition-all">
                      <td className="py-2 px-3 align-middle">
                        <div className="fw-semibold text-gray-800 text-sm">{survey.title}</div>
                        <small className="text-muted text-xs">
                          {survey.description
                            ? survey.description.length > 60
                              ? `${survey.description.substring(0, 60)}...`
                              : survey.description
                            : 'No description provided'}
                        </small>
                      </td>
                      <td className="py-2 px-3 align-middle">
                        <Badge bg={isGlobalSurvey ? 'info' : 'secondary'} className="text-capitalize">
                          {isGlobalSurvey ? 'Global' : 'Organization'}
                        </Badge>
                      </td>
                      <td className="py-2 px-3 align-middle">{getStatusBadge(survey)}</td>
                      <td className="py-2 px-3 align-middle text-gray-600 text-sm">
                        {survey.course ? (
                          <span>{survey.course.name || survey.course.title}</span>
                        ) : (
                          <span className="text-muted">Not linked</span>
                        )}
                      </td>
                      <td className="py-2 px-3 align-middle">
                        <Badge bg="primary">{survey.question_count || 0}</Badge>
                      </td>
                      <td className="py-2 px-3 align-middle text-gray-600 text-xs">
                        {survey.createdAt ? new Date(survey.createdAt).toLocaleDateString() : '—'}
                      </td>
                      <td className="py-2 px-3 align-middle">
                        <div className="d-flex gap-1 justify-content-end">
                          <button
                            onClick={() => navigate(`/organization/surveys/${survey.id}/questions`)}
                            className="text-purple-600 hover:bg-purple-100 p-2 rounded-lg transition-all"
                            title={isGlobalSurvey ? 'View Questions' : 'Manage Questions'}
                          >
                            <FaList className="w-5 h-5" />
                          </button>
                          <button
                            onClick={() => navigate(`/organization/surveys/${survey.id}/responses`)}
                            className="text-green-600 hover:bg-green-100 p-2 rounded-lg transition-all"
                            title="View Responses"
                          >
                            <FaChartBar className="w-5 h-5" />
                          </button>
                          {!isGlobalSurvey && (
                            <button
                              onClick={() => handleShowModal(survey)}
                              className="text-blue-600 hover:bg-blue-100 p-2 rounded-lg transition-all"
                              title="Edit Survey"
                            >
                              <FaEdit className="w-5 h-5" />
                            </button>
                          )}
                          {!isGlobalSurvey && !survey.deleted && (
                            <button
                              onClick={() => handleDeleteSurvey(survey)}
                              className="text-red-600 hover:bg-red-100 p-2 rounded-lg transition-all"
                              title="Delete Survey"
                            >
                              <FaTrash className="w-5 h-5" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </Table>
            </div>
          )}
        </Card.Body>
      </Card>
      

      <Modal show={showModal} onHide={handleCloseModal} size="lg" centered>
        <Modal.Header closeButton closeVariant="white" className="bg-gradient-to-r from-purple-600 to-pink-600 text-white border-0 py-3 px-4 sm:px-4 sm:py-4 modal-header">
          <Modal.Title className="font-bold text-xl text-white">
            {editingSurvey ? 'Edit Survey' : 'Create Survey'}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body className="bg-gray-50 p-3 sm:p-5">
            <div className="mb-4">
              <label className="block text-sm font-semibold text-gray-700 mb-1">Survey Title <span className="text-red-500">*</span></label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                className={`w-full px-3 py-2 rounded-lg border ${formErrors.title ? 'border-red-500' : 'border-purple-200'} focus:ring-2 focus:ring-purple-500 outline-none text-sm`}
                placeholder="Enter survey title"
              />
              {formErrors.title && <p className="text-red-500 text-xs mt-1">{formErrors.title}</p>}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Status</label>
                <select
                  name="status"
                  value={formData.status}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 rounded-lg border border-purple-200 focus:ring-2 focus:ring-purple-500 outline-none text-sm"
                >
                  <option value={1}>Active</option>
                  <option value={0}>Inactive</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Link to Course (Optional)</label>
                <select
                  name="course_id"
                  value={formData.course_id}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 rounded-lg border border-purple-200 focus:ring-2 focus:ring-purple-500 outline-none text-sm"
                >
                  <option value="">No course linked</option>
                  {courses.map((course) => (
                    <option key={course.id} value={course.id}>
                      {course.name}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">Linking a course helps you target specific learners.</p>
              </div>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-semibold text-gray-700 mb-1">Description</label>
              <textarea
                rows={3}
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                className="w-full px-3 py-2 rounded-lg border border-purple-200 focus:ring-2 focus:ring-purple-500 outline-none text-sm"
                placeholder="Describe the purpose of this survey"
              />
            </div>

            <div className="mb-6">
              <label className="block text-sm font-semibold text-gray-700 mb-1">Survey Image (Optional)</label>
              <input
                type="file"
                accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                onChange={handleImageChange}
                className="w-full px-3 py-2 rounded-lg border border-purple-200 focus:ring-2 focus:ring-purple-500 outline-none text-sm"
              />
              <p className="text-xs text-gray-500 mt-1">Optional banner image (max 5MB)</p>
              {imagePreview && (
                <div className="mt-2">
                  <img
                    src={imagePreview}
                    alt="Survey"
                    className="w-full rounded-lg"
                    style={{ maxHeight: '200px', objectFit: 'cover' }}
                  />
                </div>
              )}
            </div>

            <Modal.Footer className="bg-gray-100 border-0 rounded-b-2xl p-4">
              <Row className="w-100 g-3">
                <Col md={6}>
                  <Button bsPrefix="custom-hamburger" onClick={handleCloseModal} disabled={modalLoading} className="w-full border-1 bg-gray-100 hover:bg-gray-200 text-gray-700 border-2 border-gray-300 hover:border-gray-500 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm h-full flex items-center justify-center gap-2 p-2">
                    Cancel
                  </Button>
                </Col>
                <Col md={6}>
                  <Button
                    bsPrefix="custom-hamburger"
                    type="submit"
                    disabled={modalLoading}
                    className="w-full border-1 border-purple-600 bg-white text-purple-600 hover:from-purple-700 hover:to-pink-700 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm h-full flex items-center justify-center gap-2 p-2"
                  >
                    {modalLoading ? (
                      <>
                        <Spinner animation="border" size="sm" className="me-2" />
                        Saving...
                      </>
                    ) : (
                      editingSurvey ? 'Update Survey' : 'Create Survey'
                    )}
                  </Button>
                </Col>
              </Row>
            </Modal.Footer>
          </Modal.Body>
        </Form>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
        <Modal.Header closeButton closeVariant="white" className="bg-gradient-to-r from-purple-600 to-pink-600 text-white border-0 py-3 px-4 sm:px-4 sm:py-4 modal-header">
          <Modal.Title className="font-bold text-xl text-white">Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body className="bg-gray-50 p-3 sm:p-5 text-center py-10">
          <div className="mb-4">
            <FaTrash size={60} className="text-red-500 mx-auto" />
          </div>
          <h5 className="font-bold text-gray-800 mb-3">Delete Survey</h5>
          <p className="text-gray-600">
            Are you sure you want to delete <strong>"{surveyToDelete?.title}"</strong>? This action cannot be undone.
          </p>
        </Modal.Body>
        <Modal.Footer className="bg-gray-100 border-0 rounded-b-2xl p-4">
          <Row className="w-100 g-3">
            <Col md={6}>
              <Button bsPrefix="custom-hamburger" onClick={() => setShowDeleteModal(false)} className="w-full border-1 bg-gray-100 hover:bg-gray-200 text-gray-700 border-2 border-gray-300 hover:border-gray-500 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm h-full flex items-center justify-center gap-2 p-2">
                Cancel
              </Button>
            </Col>
            <Col md={6}>
              <Button
                bsPrefix="custom-hamburger"
                onClick={confirmDelete}
                disabled={deleteLoading}
                className="w-full border-1 border-purple-600 bg-white text-purple-600 hover:from-purple-700 hover:to-pink-700 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm h-full flex items-center justify-center gap-2 p-2"
              >
                {deleteLoading ? (
                  <>
                    <Spinner size="sm" className="me-2" />
                    Deleting...
                  </>
                ) : (
                  'Delete Survey'
                )}
              </Button>
            </Col>
          </Row>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default OrgSurveyList;
